// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AppIcon, defineComponent, IconButton, Translation } from '@shared/ui-kit';
import { classNames } from '@shared/ui-kit/utils/classnames';
import {
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { useAssetOperationModal } from './use-asset-operation-modal';
import { TransferOperationForm } from '../transfer-operation-form/transfer-operation-form';
import { BuyOperationForm } from '../buy-operation-form/buy-operation-form';
import { SellOperationForm } from '../sell-operation-form/sell-operation-form';
import { OperationType } from '@shared/data-access/interfaces/operation-type';

export const AssetOperationModal = defineComponent({
  displayName: 'AssetOperationModal',
  useSetup: useAssetOperationModal,
  View: ({
    Modal,
    modal,
    icon,
    color,
    operation,
    sourceAsset,
    onCancel,
    portfolioFunds,
    onFormDataChange,
    onFormValidationChange,
    onConfirmClick,
    isConfirmDisabled,
  }) => (
    <Modal
      PaperProps={{
        className: '!max-w-4xl !max-h-[98vh] !w-full !bg-page',
      }}
      data-testid="asset-operation-modal"
    >
      <DialogTitle
        component="div"
        className="!relative flex items-center !px-6 !pb-3 !pt-6"
      >
        <AppIcon icon={icon} color={color} size={24} />
        <Typography
          variant="h5"
          className="!ml-3 truncate font-semibold !text-title-alt"
          data-testid="modal-title"
        >
          <Translation
            ns="components"
            i18nKey={`ticker-modal.title-${operation}`}
            values={{ ticker: sourceAsset.ticker ?? sourceAsset.isin }}
          />
        </Typography>
        <IconButton
          icon="Close"
          onClick={modal.onClose}
          data-testid="close-button"
          variant="icon"
          color="disabled"
          size="md"
          className="absolute right-4 top-4"
        />
      </DialogTitle>

      <DialogContent
        className={classNames('!overflow-y-auto !px-6 !pb-3 !pt-3', {
          '!min-h-[500px]': operation === OperationType.TRANSFER,
        })}
      >
        {operation === OperationType.TRANSFER && (
          <TransferOperationForm
            sourceAsset={sourceAsset}
            portfolioFunds={portfolioFunds}
            onFormDataChange={onFormDataChange}
            onFormValidationChange={onFormValidationChange}
          />
        )}
        {operation === OperationType.BUY && (
          <BuyOperationForm
            sourceAsset={sourceAsset}
            onFormDataChange={onFormDataChange}
            onFormValidationChange={onFormValidationChange}
          />
        )}
        {operation === OperationType.SELL && (
          <SellOperationForm
            sourceAsset={sourceAsset}
            onFormDataChange={onFormDataChange}
            onFormValidationChange={onFormValidationChange}
          />
        )}
      </DialogContent>

      <DialogActions className="!gap-3 !px-6 !pb-4 !pt-3">
        <Button
          variant="outlined"
          onClick={onCancel ?? modal.onClose}
          color="secondary"
          data-testid="cancel-button"
        >
          <Translation ns="common" i18nKey="common.cancel" />
        </Button>

        {operation === OperationType.TRANSFER && (
          <Button
            color="secondary"
            onClick={onConfirmClick}
            data-testid="confirm-button"
            disabled={isConfirmDisabled}
          >
            <Translation ns="components" i18nKey="ticker-modal.transfer-action" />
          </Button>
        )}
        {operation === OperationType.BUY && (
          <Button
            color="secondary"
            onClick={onConfirmClick}
            data-testid="confirm-button"
            disabled={isConfirmDisabled}
          >
            <Translation ns="components" i18nKey="ticker-modal.buy-action" />
          </Button>
        )}
        {operation === OperationType.SELL && (
          <Button
            color="secondary"
            onClick={onConfirmClick}
            data-testid="confirm-button"
            disabled={isConfirmDisabled}
          >
            <Translation ns="components" i18nKey="ticker-modal.sell-action" />
          </Button>
        )}
      </DialogActions>
    </Modal>
  ),
});
